import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { StudioLayout } from "@/components/StudioLayout";
import { useBrandAccent } from "@/context/BrandContext";
import { FileUpload } from "@/components/FileUpload";
import { CHANNELS } from "@/lib/constants";
import { CalendarDays, Copy, RefreshCw, Trash2, Power, Check } from "lucide-react";
import { toast } from "sonner";
import { fmtTs } from "@/lib/format";

export default function BrandDetail() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { setAccent } = useBrandAccent();
  const [brand, setBrand] = useState(null);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [brandId]);

  const load = async () => {
    const { data } = await api.get(`/brands/${brandId}`);
    setBrand(data);
    setAccent(data.color);
    const m = await api.get(`/brands/${brandId}/members`);
    setMembers(m.data);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/brands/${brandId}`, brand);
      setBrand(data);
      setAccent(data.color);
      toast.success("Guardado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggleChannel = (key) => setBrand((b) => ({ ...b, channels: b.channels.includes(key) ? b.channels.filter((c) => c !== key) : [...b.channels, key] }));

  const uploadLogo = async (data) => { setBrand((b) => ({ ...b, logo_url: data.url })); };

  if (!brand) return <StudioLayout><div className="py-20 text-center text-subtle">Cargando…</div></StudioLayout>;

  return (
    <StudioLayout brand={brand}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl sm:text-5xl">{brand.name}</h1>
        <button onClick={() => navigate(`/brand/${brandId}/calendar`)} data-testid="open-calendar-btn" className="flex items-center gap-2 px-4 py-2.5 text-white text-sm rounded-sm" style={{ background: "var(--brand-color)" }}>
          <CalendarDays size={16} /> Ver calendario
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Fields */}
        <section className="space-y-5">
          <h2 className="text-lg border-b border-line pb-2">Ficha</h2>
          <Field label="Nombre"><input value={brand.name} onChange={(e) => setBrand({ ...brand, name: e.target.value })} data-testid="edit-name" className="inp" /></Field>
          <div className="flex gap-4">
            <Field label="Color principal" className="flex-1">
              <div className="flex items-center gap-2">
                <input type="color" value={brand.color} onChange={(e) => setBrand({ ...brand, color: e.target.value })} data-testid="edit-color" className="w-10 h-10 border border-line rounded-sm" />
                <input value={brand.color} onChange={(e) => setBrand({ ...brand, color: e.target.value })} className="inp font-data" />
              </div>
            </Field>
            <Field label="Zona horaria" className="flex-1"><input value={brand.timezone} onChange={(e) => setBrand({ ...brand, timezone: e.target.value })} className="inp font-data" /></Field>
          </div>
          <Field label="Logo">
            {brand.logo_url && <img src={fileUrl(brand.logo_url)} alt="" className="w-20 h-20 object-cover border border-line rounded-sm mb-2" />}
            <FileUpload onUploaded={uploadLogo} />
          </Field>
          <div>
            <span className="text-sm text-subtle">Canales activos</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {CHANNELS.map((c) => (
                <button key={c.key} onClick={() => toggleChannel(c.key)} data-testid={`edit-channel-${c.key}`}
                  className={`text-xs px-2.5 py-1.5 border rounded-sm ${brand.channels.includes(c.key) ? "bg-ink text-white border-ink" : "border-line text-subtle"}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <Field label="Notas"><textarea value={brand.notes} onChange={(e) => setBrand({ ...brand, notes: e.target.value })} rows={3} className="inp resize-none" /></Field>
          <button onClick={save} disabled={saving} data-testid="save-brand" className="px-5 py-2.5 bg-ink text-white text-sm rounded-sm disabled:opacity-50">Guardar cambios</button>
        </section>

        {/* Members */}
        <section className="space-y-5">
          <h2 className="text-lg border-b border-line pb-2">Miembros del cliente</h2>
          <AddMember brandId={brandId} onAdded={(m) => setMembers((ms) => [...ms, m])} />
          <div className="space-y-3">
            {members.length === 0 && <p className="text-sm text-subtle">Aún no hay miembros. Añade uno para darle acceso al portal.</p>}
            {members.map((m) => <MemberRow key={m.id} m={m} onChange={load} />)}
          </div>
        </section>
      </div>
    </StudioLayout>
  );
}

function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-sm text-subtle">{label}</span><div className="mt-1">{children}</div></label>;
}

function AddMember({ brandId, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const add = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Completa nombre y correo."); return; }
    try {
      const { data } = await api.post(`/brands/${brandId}/members`, form);
      onAdded(data);
      setForm({ name: "", email: "" });
      toast.success("Miembro añadido");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex gap-2">
      <input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" className="inp" />
      <input placeholder="Correo" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" className="inp" />
      <button onClick={add} data-testid="add-member" className="px-4 bg-ink text-white text-sm rounded-sm shrink-0">Añadir</button>
    </div>
  );
}

function MemberRow({ m, onChange }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/c/${m.token}`;

  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const regen = async () => { await api.post(`/members/${m.id}/regenerate-token`); toast.success("Nuevo enlace generado. El anterior deja de funcionar."); onChange(); };
  const toggle = async () => { await api.patch(`/members/${m.id}/toggle`); onChange(); };
  const del = async () => { await api.delete(`/members/${m.id}`); onChange(); };

  return (
    <div className="border border-line bg-surface p-3.5 rounded-sm" data-testid={`member-${m.id}`}>
      <div className="flex items-center gap-2">
        <span style={{ width: 8, height: 8, background: m.active ? "#3F6B4F" : "#6E6E73", borderRadius: "50%" }} />
        <span className="font-medium text-sm">{m.name}</span>
        <span className="text-subtle text-sm">· {m.email}</span>
      </div>
      <div className="text-xs text-subtle mt-1 font-data">Último acceso: {m.last_access ? fmtTs(m.last_access) : "nunca"}</div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button onClick={copy} data-testid={`copy-link-${m.id}`} className="text-xs flex items-center gap-1 border border-line rounded-sm px-2 py-1">
          {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copiado" : "Copiar enlace"}
        </button>
        <button onClick={regen} className="text-xs flex items-center gap-1 border border-line rounded-sm px-2 py-1"><RefreshCw size={12} /> Regenerar</button>
        <button onClick={toggle} className="text-xs flex items-center gap-1 border border-line rounded-sm px-2 py-1"><Power size={12} /> {m.active ? "Revocar" : "Activar"}</button>
        <button onClick={del} className="text-xs flex items-center gap-1 border border-line rounded-sm px-2 py-1 text-changes"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}
